'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  Check,
  ExternalLink,
  Flag,
  MapPin,
  Package,
  Radio,
  RefreshCw,
  SearchX,
  Truck,
  X,
} from 'lucide-react';
import { ActionIcon, Badge, Button, Modal, Popover, Text, Title } from 'rizzui';
import type {
  PublicShipment,
  PublicShipmentFreight,
  PublicShipmentLocation,
  PublicShipmentTimelineItem,
} from '@/app/shared/landing/tracking/public-shipment-types';
import { Skeleton } from '@core/ui/skeleton';

function parsePublicDate(value?: string | number | null) {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number') {
    const ms = value < 1e11 ? value * 1000 : value;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const str = String(value).trim();
  if (/^\d+$/.test(str)) {
    const num = Number(str);
    const ms = num < 1e11 ? num * 1000 : num;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(str)
    ? `${str}T12:00:00`
    : str.replace(' ', 'T');
  const date = new Date(normalized);

  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateTime(value?: string | number | null) {
  if (value === null || value === undefined || value === '') return '';

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
    if (match) {
      return `${match[1]} ${match[2]} UTC`;
    }
  }

  const date = parsePublicDate(value);
  if (!date) return typeof value === 'string' ? value : '';

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');

  return `${y}-${m}-${d} ${h}:${min} UTC`;
}

function formatTimelineDate(value?: string | number | null) {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
    if (match) {
      return {
        date: match[1],
        time: `${match[2]} UTC`,
      };
    }
  }

  const date = parsePublicDate(value);
  if (!date) return null;

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');

  return {
    date: `${y}-${m}-${d}`,
    time: `${h}:${min} UTC`,
  };
}

function humanize(value?: string | null) {
  if (!value) return '';

  return value
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatLocation(location?: PublicShipmentLocation | null) {
  if (!location) return null;

  return (
    [location.city, location.state].filter(Boolean).join(', ') ||
    location.address ||
    null
  );
}

function formatEquipment(shipment: PublicShipment) {
  const equipment = shipment.freight?.equipment_type;
  if (!equipment) return null;
  if (typeof equipment === 'string') return equipment;
  if (typeof equipment === 'object') {
    return (
      [equipment.code, equipment.description].filter(Boolean).join(' · ') ||
      null
    );
  }
  return null;
}

function formatWeight(weight?: number | string | null) {
  if (weight === null || weight === undefined || weight === '') return null;
  const num =
    typeof weight === 'number'
      ? weight
      : Number(String(weight).replace(/,/g, ''));
  if (Number.isNaN(num)) return String(weight);

  return `${new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(num)} lbs`;
}

function formatHazmat(value?: boolean | string | unknown) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') return humanize(value);
  if (typeof value === 'object') return 'Yes';

  return String(value);
}

function formatTemperature(
  value?: PublicShipmentFreight['temperature'] | null
) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') return `${value}°`;
  if (typeof value === 'string') return value;

  const unit = value.type?.toLowerCase().startsWith('fahrenheit')
    ? '°F'
    : value.type?.toLowerCase().startsWith('celsius')
      ? '°C'
      : value.type
        ? ` ${value.type}`
        : '';
  const min = value.min === null || value.min === undefined ? '' : value.min;
  const max = value.max === null || value.max === undefined ? '' : value.max;
  const range =
    min !== '' && max !== ''
      ? min === max
        ? `${min}${unit}`
        : `${min}-${max}${unit}`
      : min !== ''
        ? `Min ${min}${unit}`
        : max !== ''
          ? `Max ${max}${unit}`
          : '';

  return (
    [value.required ? 'Required' : null, range].filter(Boolean).join(', ') ||
    null
  );
}

function safeTrackingUrl(value?: string | null) {
  if (!value) return null;

  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? value : null;
  } catch {
    return null;
  }
}

function SummaryRow({ label, value }: { label: string; value: ReactNode }) {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && !value.trim())
  ) {
    return null;
  }

  return (
    <div className="flex flex-col gap-0.5 border-b border-[#edf1f5] py-2.5 last:border-b-0 sm:flex-row sm:items-baseline sm:gap-6">
      <dt className="text-xs leading-5 text-[#718096] sm:w-36 sm:shrink-0 sm:text-sm sm:leading-6">
        {label}
      </dt>
      <dd className="min-w-0 flex-1 break-words text-sm font-bold leading-6 text-[#152238]">
        {value}
      </dd>
    </div>
  );
}

function TimelineIcon({ milestoneKey }: { milestoneKey?: string | null }) {
  const normalizedKey = milestoneKey ?? '';

  if (normalizedKey.includes('arrived')) {
    return <MapPin className="h-5 w-5" />;
  }
  if (normalizedKey.includes('delivery')) {
    return <Package className="h-5 w-5" />;
  }
  if (normalizedKey.includes('pickup') || normalizedKey.includes('transit')) {
    return <Truck className="h-5 w-5" />;
  }

  return <Radio className="h-5 w-5" />;
}

function TrackingStep({
  item,
  isLatest,
  connectorPassed,
  isLast,
}: {
  item: PublicShipmentTimelineItem;
  isLatest: boolean;
  connectorPassed: boolean;
  isLast: boolean;
}) {
  const isCompleted = item.status?.toLowerCase() === 'completed';
  const occurredAt = formatTimelineDate(item.occurred_at);

  return (
    <li className="relative min-w-[150px] flex-1 snap-center text-center">
      {!isLast ? (
        <span
          aria-hidden="true"
          className={`absolute left-[calc(50%+27px)] right-[calc(-50%+27px)] top-6 h-0.5 ${
            connectorPassed ? 'bg-[#09243f]' : 'bg-[#dbe4ec]'
          }`}
        />
      ) : null}

      <div className="relative z-[1] mx-auto flex h-12 w-12 items-center justify-center">
        <span
          aria-hidden="true"
          className={`flex h-11 w-11 items-center justify-center rounded-full border-2 ${
            isCompleted
              ? isLatest
                ? 'border-[#09243f] bg-white text-[#09243f] ring-4 ring-[#eef6fc]'
                : 'border-[#09243f] bg-[#09243f] text-white'
              : 'border-[#dbe4ec] bg-white text-[#91a0b2]'
          }`}
        >
          {isCompleted && !isLatest ? (
            <Check className="h-5 w-5" strokeWidth={3} />
          ) : (
            <TimelineIcon milestoneKey={item.key} />
          )}
        </span>
        {isLatest ? (
          <span className="absolute -right-0.5 top-0.5 h-3.5 w-3.5 rounded-full border-[3px] border-white bg-[#09243f]" />
        ) : null}
      </div>

      <div className="min-w-0">
        <p className="mt-2 text-sm font-bold leading-5 text-[#111827]">
          {item.label || humanize(item.key) || 'Shipment update'}
        </p>
        {occurredAt ? (
          <>
            <p className="mt-1 text-sm leading-5 text-[#718096]">
              {occurredAt.date}
            </p>
            <p className="text-sm leading-5 text-[#718096]">
              {occurredAt.time}
            </p>
          </>
        ) : (
          <p className="mt-1 text-sm leading-5 text-[#9aabc0]">
            {humanize(item.status)}
          </p>
        )}
      </div>
    </li>
  );
}

function RouteSummaryRow({
  type,
  location,
  isLast,
}: {
  type: 'pickup' | 'delivery';
  location: PublicShipmentLocation | null;
  isLast: boolean;
}) {
  if (!location) return null;

  const isPickup = type === 'pickup';
  const locationDetails = [formatLocation(location), location.address].filter(
    (value, index, values): value is string => {
      return Boolean(value) && values.indexOf(value) === index;
    }
  );
  const estimatedTime = location.scheduled_at
    ? formatDateTime(location.scheduled_at)
    : null;

  if (!locationDetails.length && !estimatedTime) return null;

  const locationContent = locationDetails.map((detail, index) => (
    <span key={detail}>
      {index > 0 ? (
        <span aria-hidden="true" className="text-[#c1cbd6]">
          {' / '}
        </span>
      ) : null}
      <span className="text-sm font-semibold text-[#334155]">{detail}</span>
    </span>
  ));

  return (
    <li className="relative flex min-w-0 items-center pb-7 last:pb-0 sm:pb-8">
      {!isLast ? (
        <span
          aria-hidden="true"
          className="absolute -bottom-[18px] left-[19px] top-10 w-0.5 bg-gradient-to-b from-emerald-400 to-sky-400 sm:-bottom-[22px] sm:left-[21px] sm:top-11"
        />
      ) : null}
      <div className="flex w-full min-w-0 items-start gap-3 sm:items-center sm:gap-4">
        <span
          aria-hidden="true"
          className={`relative z-[1] grid h-10 w-10 shrink-0 place-items-center rounded-full border-4 border-white shadow-sm sm:h-11 sm:w-11 ${
            isPickup
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-sky-100 text-sky-700'
          }`}
        >
          {isPickup ? (
            <Package className="h-4 w-4" />
          ) : (
            <Flag className="h-4 w-4" />
          )}
        </span>

        <div className="min-w-0 flex-1 md:flex md:items-center md:gap-4">
          <div className="min-w-0">
            <p
              className={`text-[11px] font-bold uppercase tracking-[0.13em] sm:text-xs ${
                isPickup ? 'text-emerald-700' : 'text-sky-700'
              }`}
            >
              {isPickup ? 'Pickup' : 'Delivery'}
            </p>
            {locationDetails.length ? (
              <p className="mt-1 max-w-full break-words leading-6 [overflow-wrap:anywhere] sm:mt-1.5">
                {locationContent}
              </p>
            ) : null}
          </div>

          {estimatedTime ? (
            <div className="mt-3 shrink-0 md:ml-auto md:mt-0 md:text-right">
              <p className="text-[10px] font-bold uppercase tracking-[0.13em] text-[#91a0b2]">
                Estimated time
              </p>
              <p className="mt-1 text-sm font-semibold text-[#334155] sm:mt-1.5">
                {estimatedTime}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function FreightDetailsPopover({
  shipment,
  description,
  equipment,
}: {
  shipment: PublicShipment;
  description: string | null;
  equipment: string | null;
}) {
  const { freight } = shipment;

  return (
    <Popover placement="bottom-end" shadow="sm">
      <Popover.Trigger>
        <Button
          type="button"
          variant="outline"
          className="h-11 !w-full whitespace-nowrap rounded-lg border-[#dce5ed] px-4 text-sm font-bold text-[#334155] shadow-none sm:h-10 sm:!w-auto"
        >
          <Package className="me-2 h-4 w-4 text-[#718096]" />
          Freight details
        </Button>
      </Popover.Trigger>
      <Popover.Content className="z-[10050] max-h-[min(70dvh,520px)] w-[430px] max-w-[calc(100vw-1.5rem)] overflow-y-auto rounded-2xl border border-[#dce5ed] bg-white p-4 shadow-[0_18px_55px_rgba(7,24,47,0.2)] sm:p-5">
        <Title as="h4" className="text-lg font-bold text-[#111827]">
          Freight details
        </Title>
        <dl className="mt-3">
          <SummaryRow label="Description" value={description} />
          <SummaryRow label="Weight" value={formatWeight(freight?.weight)} />
          <SummaryRow label="Pieces" value={freight?.pieces} />
          <SummaryRow label="Pallets" value={freight?.pallets} />
          <SummaryRow label="Equipment" value={equipment} />
          <SummaryRow
            label="Temperature"
            value={formatTemperature(freight?.temperature)}
          />
          <SummaryRow label="Hazmat" value={formatHazmat(freight?.hazmat)} />
        </dl>
      </Popover.Content>
    </Popover>
  );
}

function ShipmentTrackingCard({ shipment }: { shipment: PublicShipment }) {
  const { freight, pickup, delivery, tracking } = shipment;
  const timeline = Array.isArray(shipment.timeline) ? shipment.timeline : [];
  const timelineRef = useRef<HTMLDivElement>(null);
  const [scrollEdges, setScrollEdges] = useState({ left: false, right: false });
  const updateScrollEdges = useCallback(() => {
    const element = timelineRef.current;
    if (!element) return;

    setScrollEdges({
      left: element.scrollLeft > 2,
      right: element.scrollLeft + element.clientWidth < element.scrollWidth - 2,
    });
  }, []);

  useEffect(() => {
    const element = timelineRef.current;
    if (!element) return;

    updateScrollEdges();
    const observer = new ResizeObserver(updateScrollEdges);
    observer.observe(element);
    return () => observer.disconnect();
  }, [timeline.length, updateScrollEdges]);

  const scrollTimeline = (direction: -1 | 1) => {
    timelineRef.current?.scrollBy({
      left: direction * Math.min(timelineRef.current.clientWidth * 0.75, 330),
      behavior: 'smooth',
    });
  };
  const trackingUrl = safeTrackingUrl(tracking?.tracking_url);
  const equipment = formatEquipment(shipment);
  const latestCompletedIndex = timeline.reduce(
    (latest, item, index) =>
      item.status?.toLowerCase() === 'completed' ? index : latest,
    -1
  );
  const freightDescription =
    freight?.description || shipment.shipment?.description || null;
  const hasFreightDetails = Boolean(
    freightDescription ||
      (freight?.weight !== null &&
        freight?.weight !== undefined &&
        freight?.weight !== '') ||
      (freight?.pieces !== null &&
        freight?.pieces !== undefined &&
        freight?.pieces !== '') ||
      (freight?.pallets !== null &&
        freight?.pallets !== undefined &&
        freight?.pallets !== '') ||
      equipment ||
      formatTemperature(freight?.temperature) ||
      formatHazmat(freight?.hazmat)
  );

  return (
    <section className="overflow-hidden bg-white">
      <div className="mb-5 sm:mb-6 sm:flex sm:items-center sm:justify-end">
        <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:items-center">
          {hasFreightDetails ? (
            <FreightDetailsPopover
              shipment={shipment}
              description={freightDescription}
              equipment={equipment}
            />
          ) : null}
          {tracking?.available && trackingUrl ? (
            <>
              {/* RizzUI Button cannot render an anchor; the native link is required
                  for secure external navigation while Button preserves the UI style. */}
              <a
                href={trackingUrl}
                target="_blank"
                rel="noreferrer"
                className="w-full sm:w-auto"
              >
                <Button
                  as="span"
                  className="h-11 !w-full rounded-lg !bg-[#03a1fe] px-4 text-sm font-bold !text-white shadow-none hover:!bg-[#008de5] hover:!text-white sm:h-10 sm:!w-auto"
                  style={{ color: '#ffffff', WebkitTextFillColor: '#ffffff' }}
                >
                  Open live tracking
                  <ExternalLink
                    className="ms-2 h-4 w-4 !text-white"
                    style={{ color: '#ffffff' }}
                  />
                </Button>
              </a>
            </>
          ) : null}
        </div>
      </div>
      {timeline.length ? (
        <div className="relative border-y border-[#edf1f5]">
          <div
            ref={timelineRef}
            onScroll={updateScrollEdges}
            role="region"
            aria-label="Shipment tracking progress"
            tabIndex={0}
            className="touch-pan-x overflow-x-auto overscroll-x-contain py-5 [scrollbar-width:thin] md:py-6"
          >
            <ol
              dir="ltr"
              className="flex snap-x snap-mandatory flex-row"
              style={{ minWidth: `${Math.max(timeline.length, 4) * 165}px` }}
            >
              {timeline.map((item, index) => {
                const nextItem = timeline[index + 1];
                const connectorPassed =
                  item.status?.toLowerCase() === 'completed' &&
                  nextItem?.status?.toLowerCase() === 'completed';

                return (
                  <TrackingStep
                    key={item.key || `${item.label || 'milestone'}-${index}`}
                    item={item}
                    isLatest={index === latestCompletedIndex}
                    connectorPassed={connectorPassed}
                    isLast={index === timeline.length - 1}
                  />
                );
              })}
            </ol>
          </div>
          {scrollEdges.left ? (
            <div className="pointer-events-none absolute inset-y-0 -left-px z-10 flex w-[calc(3rem+1px)] items-center justify-start bg-gradient-to-r from-white via-white/85 to-transparent pl-1 backdrop-blur-[1px] sm:w-[calc(4rem+1px)] sm:pl-2">
              <ActionIcon
                type="button"
                aria-label="Scroll tracking steps left"
                variant="outline"
                rounded="full"
                onClick={() => scrollTimeline(-1)}
                className="pointer-events-auto !h-9 !w-9 border-[#dce5ed] !bg-white/95 !text-[#09243f] shadow-sm"
              >
                <ChevronLeft className="h-5 w-5" />
              </ActionIcon>
            </div>
          ) : null}
          {scrollEdges.right ? (
            <div className="pointer-events-none absolute inset-y-0 -right-px z-10 flex w-[calc(3rem+1px)] items-center justify-end bg-gradient-to-l from-white via-white/85 to-transparent pr-1 backdrop-blur-[1px] after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-[3px] after:bg-white sm:w-[calc(4rem+1px)] sm:pr-2">
              <ActionIcon
                type="button"
                aria-label="Scroll tracking steps right"
                variant="outline"
                rounded="full"
                onClick={() => scrollTimeline(1)}
                className="pointer-events-auto !h-9 !w-9 border-[#dce5ed] !bg-white/95 !text-[#09243f] shadow-sm"
              >
                <ChevronRight className="h-5 w-5" />
              </ActionIcon>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="my-5 grid gap-4 sm:my-6 lg:grid-cols-2">
        {pickup || delivery ? (
          <section className="rounded-2xl border border-[#dce5ed] p-4 sm:p-5 lg:col-span-2">
            <div>
              <ol>
                <RouteSummaryRow
                  type="pickup"
                  location={pickup}
                  isLast={!delivery}
                />
                <RouteSummaryRow type="delivery" location={delivery} isLast />
              </ol>
            </div>
          </section>
        ) : null}
      </div>
    </section>
  );
}

function ShipmentDetailsSkeleton() {
  return (
    <div
      role="status"
      aria-label="Looking up shipment"
      className="home-shipment-modal-skeleton overflow-hidden rounded-[1.4rem] border border-[#dce5ed] bg-white"
    >
      <span className="sr-only">Looking up shipment…</span>

      <div className="flex flex-col gap-5 border-b border-[#edf1f5] p-5 lg:flex-row lg:items-center lg:justify-between lg:p-7">
        <div className="flex items-center gap-3">
          <Skeleton className="h-12 w-12 shrink-0 rounded-xl" />
          <div>
            <div className="flex items-center gap-3">
              <Skeleton className="h-5 w-40 rounded-md" />
              <Skeleton className="h-7 w-24 rounded-full" />
            </div>
            <Skeleton className="mt-3 h-3.5 w-52 rounded-md" />
          </div>
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-10 w-28 rounded-lg" />
          <Skeleton className="h-10 w-36 rounded-lg" />
        </div>
      </div>

      <div className="touch-pan-x overflow-x-auto overscroll-x-contain border-b border-[#edf1f5] px-5 py-7 [scrollbar-width:thin] lg:px-10">
        <div className="flex min-w-[660px] items-start justify-between">
          {[0, 1, 2, 3].map((step) => (
            <div key={step} className="flex w-32 flex-col items-center">
              <Skeleton className="h-12 w-12 rounded-full" />
              <Skeleton className="mt-3 h-3.5 w-24 rounded-md" />
              <Skeleton className="mt-2 h-3 w-20 rounded-md" />
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-5 p-5 lg:grid-cols-2 lg:p-7">
        <div className="rounded-2xl border border-[#dce5ed] p-5 lg:col-span-2">
          <Skeleton className="h-5 w-32 rounded-md" />
          <div className="relative mt-5 space-y-6">
            <span className="absolute bottom-8 left-[18px] top-8 w-0.5 bg-[#e2e8f0]" />
            {[0, 1].map((point) => (
              <div key={point} className="relative flex items-center gap-3">
                <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
                <Skeleton className="h-4 flex-1 rounded-md" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ShipmentErrorState({
  message,
  onTryAgain,
}: {
  message: string;
  onTryAgain: () => void;
}) {
  const isNotFound = /not found/i.test(message);

  return (
    <section
      role="alert"
      aria-live="polite"
      className="flex min-h-[320px] flex-col items-center justify-center rounded-[1.75rem] border border-[#dbe7f0] bg-[#f7fbfe] px-6 py-12 text-center sm:min-h-[360px] sm:px-12"
    >
      <span
        aria-hidden="true"
        className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-[#e6f5ff] text-[#0397ef] ring-8 ring-[#eff9ff]"
      >
        <SearchX className="h-9 w-9" strokeWidth={1.8} />
      </span>

      <Text className="mt-7 !text-xs font-bold uppercase tracking-[0.16em] !text-[#0397ef]">
        Tracking result
      </Text>
      <Title
        as="h3"
        className="mt-3 !text-3xl font-bold tracking-[-0.035em] !text-[#111827] sm:!text-4xl"
      >
        {isNotFound ? 'Shipment not found' : 'Unable to load shipment'}
      </Title>
      <Text className="mt-4 max-w-xl !text-base leading-7 !text-[#64748b] sm:!text-lg">
        {isNotFound
          ? 'No shipment matched that PRO number. Check the number and try again.'
          : message}
      </Text>

      <Button
        type="button"
        rounded="pill"
        onClick={onTryAgain}
        className="mt-8 h-12 !bg-[#09243f] px-7 font-semibold !text-white hover:!bg-[#10385e]"
      >
        Try another PRO number
      </Button>
    </section>
  );
}

export function HomeShipmentModal({
  isOpen,
  isLoading,
  error,
  shipment,
  onRefresh,
  isRefreshing = false,
  refreshError = '',
  onClose,
}: {
  isOpen: boolean;
  isLoading: boolean;
  error: string;
  shipment: PublicShipment | null;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  refreshError?: string;
  onClose: () => void;
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      noGutter
      customSize="1360px"
      rounded="xl"
      overlayClassName="bg-[#07182f]/55 backdrop-blur-[3px]"
      containerClassName="!m-0 !h-auto !max-h-[calc(100dvh-2rem)] !min-w-0 !w-[calc(100vw-2rem)] !max-w-none !rounded-[1.5rem] overflow-y-auto border border-black/[0.07] !bg-white p-0 shadow-[0_28px_90px_rgba(7,24,47,0.28)] sm:!max-h-[calc(100dvh-2rem)] sm:!w-[calc(100vw-2rem)] lg:!w-[92vw]"
      className="z-[10000]"
    >
      <header className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-black/[0.07] !bg-white/95 px-4 py-4 backdrop-blur sm:gap-4 sm:px-8 sm:py-6 lg:px-10 lg:py-7">
        <div className="min-w-0">
          <Text className="!text-xs font-bold uppercase tracking-[0.16em] !text-[#0397ef]">
            Shipment lookup
          </Text>
          <Title
            as="h2"
            className="mt-1 !text-[1.4rem] font-bold leading-[1.15] tracking-[-0.04em] !text-[#111214] sm:!text-[1.85rem] lg:!text-[2.15rem]"
          >
            Ship Smart Solutions tracking
          </Title>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {onRefresh ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={isLoading || isRefreshing}
              isLoading={isRefreshing}
              aria-label="Refresh shipment tracking"
              className="!border-[#dbe7f0] !text-[#09243f] hover:!bg-[#eef6fc]"
            >
              {!isRefreshing ? (
                <RefreshCw className="me-2 h-4 w-4" aria-hidden="true" />
              ) : null}
              {isRefreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          ) : null}
          <ActionIcon
            type="button"
            aria-label="Close shipment details"
            title="Close"
            variant="text"
            rounded="full"
            onClick={onClose}
            className="-me-1 shrink-0 !text-[#64748b] hover:!bg-[#f1f5f9] hover:!text-[#0f172a] sm:me-0"
          >
            <X className="h-5 w-5" />
          </ActionIcon>
        </div>
      </header>

      <div className="p-4 sm:p-7 lg:p-10">
        {refreshError ? (
          <Text
            role="alert"
            className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 !text-sm !text-red-700"
          >
            {refreshError}
          </Text>
        ) : null}
        {isLoading ? (
          <ShipmentDetailsSkeleton />
        ) : error ? (
          <ShipmentErrorState message={error} onTryAgain={onClose} />
        ) : shipment ? (
          <>
            <ShipmentTrackingCard shipment={shipment} />
            <div className="mt-7 flex flex-col items-stretch gap-4 px-1 sm:mt-10 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
              <span className="inline-flex items-center justify-center gap-2 text-center text-xs font-semibold text-[#77797e] sm:justify-start sm:text-left">
                <Activity className="h-4 w-4 text-[#03a1fe]" />
                Data provided by Ship Smart Solutions
              </span>
              <Button
                type="button"
                variant="outline"
                rounded="pill"
                onClick={onClose}
                className="h-11 !w-full border-[#09243f] px-6 font-semibold !text-[#09243f] shadow-none hover:!border-[#09243f] hover:!bg-[#eef6fc] hover:!text-[#09243f] sm:!w-auto"
                style={{
                  borderColor: '#09243f',
                  color: '#09243f',
                  WebkitTextFillColor: '#09243f',
                }}
              >
                Close
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
